/**
 * CSRF token utilities for the Audit Scrubber frontend.
 *
 * WHAT
 *   - getCsrfToken(): reads the `scrubber_csrf` cookie that the server
 *     sets on every GET /api/* response. The cookie is non-httpOnly by
 *     design so the browser JS can echo it back as a header.
 *   - isCsrfExempt(url): returns true for paths the server explicitly
 *     excludes from CSRF enforcement, so we don't pollute those requests
 *     with an irrelevant header.
 *   - csrfFetch(url, options): a thin wrapper over `fetch` that injects
 *     the `X-CSRF-Token` header on state-changing requests targeting
 *     `/api/*`. Falls back to a plain fetch if the cookie is missing
 *     (the server is in warn-only mode and will log, not block).
 *
 * WHY
 *   The Scrubber server-side CSRF middleware is deployed in warn-only
 *   mode (`SCRUBBER_CSRF_ENFORCEMENT=off`). It logs `csrf_warn_only`
 *   audit events when state-changing requests arrive without the header.
 *   Once we drive that signal to zero for seven straight days, the env
 *   var flips to `on` and missing headers become 403s. This module is
 *   what gets us to zero warns.
 *
 * SERVER CONTRACT (do not change without coordinating with the API)
 *   - Cookie name: `scrubber_csrf` (non-httpOnly, 64-char hex)
 *   - Header name: `X-CSRF-Token` (constant-time compared)
 *   - Methods checked: POST, PUT, PATCH, DELETE
 *   - Exempt prefixes: `/api/fdr/portal/`, `/agent/`
 *   - Exempt exact:    `/api/ping`
 *
 * MIGRATION STATUS (as of 2026-05-21)
 *   Every non-exempt state-changing call site in the React app has been
 *   switched to `csrfFetch` (see the patched files listed in the change
 *   log). The remaining `fetch()` calls that touch `/api/*` fall into
 *   two groups, both safe to leave alone:
 *     1. GETs — server only enforces CSRF on POST/PUT/PATCH/DELETE.
 *     2. `/api/fdr/portal/*` calls in components/fdr/FDRPortal.jsx and
 *        FDRUniverseReview.jsx — exempt by server contract because the
 *        portal uses its own magic-link session, not the org cookie.
 *   If you add a new state-changing call to `/api/*`, use `csrfFetch`.
 */

const EXEMPT_PREFIXES = ['/api/fdr/portal/', '/agent/'];
const EXEMPT_EXACT = new Set(['/api/ping']);
const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let _warnedMissingCookieThisSession = false;

export function getCsrfToken() {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const m = document.cookie.match(/(?:^|;\s*)scrubber_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Returns true if `url` targets a path the server explicitly exempts
 * from CSRF checks. Handles absolute URLs, relative paths, and URLs
 * carrying a querystring or fragment.
 */
export function isCsrfExempt(url) {
  if (!url) return false;
  let path;
  try {
    // Absolute URLs (http://..., https://...)
    path = new URL(url, 'http://_local_').pathname;
  } catch (_) {
    path = String(url).split('?')[0].split('#')[0];
  }
  if (EXEMPT_EXACT.has(path)) return true;
  for (const prefix of EXEMPT_PREFIXES) {
    if (path.startsWith(prefix)) return true;
  }
  return false;
}

/**
 * Drop-in replacement for `fetch` that echoes the CSRF cookie back
 * as `X-CSRF-Token` on state-changing requests aimed at `/api/*`.
 *
 * Behavior:
 *   - GET / HEAD / OPTIONS: passes through untouched (server only
 *     checks state-changing methods)
 *   - Exempt paths: passes through untouched
 *   - Cookie missing: warns once per session, then sends the request
 *     without the header. The server's warn-only mode will record it.
 *   - Cookie present: adds `X-CSRF-Token` without disturbing any
 *     headers the caller already set.
 *
 * Returns the same Promise<Response> as native fetch.
 */
export function csrfFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrf = STATE_CHANGING_METHODS.has(method) && !isCsrfExempt(url);

  if (!needsCsrf) {
    return fetch(url, options);
  }

  const token = getCsrfToken();
  if (!token) {
    if (!_warnedMissingCookieThisSession && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        '[csrf] scrubber_csrf cookie not found; sending request without ' +
        'X-CSRF-Token. Server is in warn-only mode and will log this.'
      );
      _warnedMissingCookieThisSession = true;
    }
    return fetch(url, options);
  }

  // Preserve any headers the caller passed (Headers instance, plain
  // object, or array of pairs) and layer X-CSRF-Token on top.
  const merged = new Headers(options.headers || {});
  merged.set('X-CSRF-Token', token);

  return fetch(url, { ...options, headers: merged });
}

// Internal export for tests only.
export const __test = { EXEMPT_PREFIXES, EXEMPT_EXACT, STATE_CHANGING_METHODS };
